import { createQuery } from "@urql/solid"
import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  type ParentProps,
  useContext,
} from "solid-js"
import { graphql, type ResultOf } from "@/graphql"
import { betterAuthClient } from "@/lib/better-auth"
import { identifyUser } from "@/lib/observability"
import { registerUserSuspendedHandler } from "@/lib/urql-client"

interface LoggedInState {
  status: "logged-in"
  user: User
}

interface LoadingState {
  status: "loading"
  user: null
}

interface LoggedOutState {
  status: "logged-out"
  user: null
}

type SessionState = LoggedInState | LoadingState | LoggedOutState

interface SessionActions {
  refetchSession: () => Promise<void>
  signOut: () => Promise<boolean>
  isSigningOut: () => boolean
}

interface SessionContextValue {
  session: () => SessionState
  isSuspended: () => boolean
  actions: SessionActions
}

const SessionContext = createContext<SessionContextValue>()

export function createSession() {
  const context = useContext(SessionContext)

  if (!context) {
    throw new Error("createSession must be used within SessionProvider")
  }

  return context
}

const UserFragment = graphql(`
  fragment UserFragment on Me @_unmask {
    id
    email
    emailVerified
    onboardingStage
    interfaceLocale
    authorName
    readerOnboarded
    interestCategories
    signupSource
    disabledAt
    country
    publication {
      id
      name
      title
      locale
      url
      logoUrl
    }
  }
`)

export type User = ResultOf<typeof UserFragment>

export function SessionProvider(props: ParentProps) {
  const UserQuery = graphql(
    `query SessionProvider {
      me {
        ...UserFragment
      }
    }`,
    [UserFragment],
  )

  const [userQuery, refetchUser] = createQuery({
    query: UserQuery,
    requestPolicy: "network-only",
  })

  const [isSuspended, setIsSuspended] = createSignal(false)

  onMount(() =>
    onCleanup(registerUserSuspendedHandler(() => setIsSuspended(true))),
  )

  const [isSigningOut, setIsSigningOut] = createSignal(false)
  async function signOut() {
    setIsSigningOut(true)
    await betterAuthClient().signOut()
    window.location.href = import.meta.env.VITE_FIKA_PUBLIC_URL || "/"
    return true
  }

  createEffect(() => {
    const user = userQuery.data?.me
    if (!user) return

    if (user.disabledAt) return

    setIsSuspended(false)
    identifyUser({
      id: user.id,
      email: user.email,
      name: user.publication?.name ?? null,
    })
  })

  const session = createMemo((): SessionState => {
    if (isSuspended()) return { status: "logged-out", user: null }

    if (userQuery.fetching) {
      return {
        status: "loading",
        user: null,
      }
    }

    const me = userQuery.data?.me
    if (me && !me.disabledAt) {
      return {
        status: "logged-in",
        user: me,
      }
    }

    return {
      status: "logged-out",
      user: null,
    }
  })

  return (
    <SessionContext.Provider
      value={{
        session,
        isSuspended,
        actions: {
          isSigningOut,
          signOut,
          refetchSession: async () => {
            await refetchUser({ requestPolicy: "network-only" })
          },
        },
      }}
    >
      {props.children}
    </SessionContext.Provider>
  )
}

export const ApplicationContext = createContext<{
  user: User
}>()

export const useApplication = () => {
  const context = useContext(ApplicationContext)

  if (!context) {
    throw new Error("useApplication must be used within ApplicationContext")
  }

  return context
}
